import * as Sentry from '@sentry/react';
import { RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProfileSettings,
  setShowSelfieVerificationWall,
  skipShowSelfieVerificationWallCheck,
} from '../actions/profileActions';
import useVerifyEmailWallModal from './useVerifyEmailWallModal';
import useCompleteProfileModal from './useCompleteProfileModal';

interface IUseMandatoryWall {
  showMandatoryWall(
    callback?: () => void,
    callbackAfterValidate?: () => void,
    action?: string,
    shouldForceRunCallback?: boolean,
    excludes?: string[]
  ): void;
}

const useMandatoryWall = (): IUseMandatoryWall => {
  const dispatch = useDispatch();
  const { showMandatoryVerifyEmailWall } = useVerifyEmailWallModal();
  const { showMandatoryCompleteProfile } = useCompleteProfileModal();
  const account = useSelector((state: RootState) => state.profile);

  const showMandatoryWall = async (
    callback = () => undefined,
    callbackAfterValidate = () => undefined,
    action = '',
    shouldForceRunCallback = false,
    excludes: string[] = []
  ) => {
    try {
      if (account === null) {
        return;
      }

      // Check if user is not completed check selfie verification
      if (!excludes.includes('selfieWall')) {
        const isSelfieRequired = isSelfieVerificationRequired(account?.liveness);

        if (isSelfieRequired) {
          callbackAfterValidate && callbackAfterValidate();
          return;
        }

        const skipLivenessFor = account?.skipLivenessFor ?? 0;
        const isSkipLivenessExpired = skipLivenessFor < new Date().getTime();

        if (isSkipLivenessExpired) {
          const settings = await dispatch(getProfileSettings());

          if (isSelfieVerificationRequired(settings?.mandatory?.liveness)) {
            callbackAfterValidate && callbackAfterValidate();
            return;
          } else {
            dispatch(skipShowSelfieVerificationWallCheck());
          }

          isSelfieVerificationCompleted(settings?.liveness);
        }
      }

      if (action === 'navigate') return;

      // Check if user completed mandatory email
      const shouldVerifyEmail = isVerifyEmailRequired(account);

      // Check if user completed profile mandatory
      const isProfileComplete = account.mandatory_profile_completed;

      if (shouldVerifyEmail && !excludes.includes('emailWall')) {
        const checkProfileWall = !isProfileComplete && !excludes.includes('profileWall');

        showMandatoryVerifyEmailWall(
          () =>
            checkProfileWall
              ? showMandatoryCompleteProfile(callback, callbackAfterValidate, action, shouldForceRunCallback)
              : callback(),
          callbackAfterValidate()
        );
        return;
      }

      if (!isProfileComplete && !excludes.includes('profileWall')) {
        showMandatoryCompleteProfile(callback, callbackAfterValidate, action, shouldForceRunCallback);
        return;
      }

      callback();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const isSelfieVerificationCompleted = (liveness): boolean => {
    const isCompleted =
      typeof liveness?.completed !== 'undefined'
        ? liveness.completed
        : !['not_started', 'required'].includes(liveness?.status);

    if (isCompleted) {
      dispatch(setShowSelfieVerificationWall(false));
    }

    return isCompleted;
  };

  const isSelfieVerificationRequired = (liveness): boolean => {
    const isRequired =
      ['not_started', 'required'].includes(liveness?.status) &&
      (liveness?.required || (liveness?.required && liveness?.skippable));

    if (isRequired) {
      dispatch(setShowSelfieVerificationWall(true));
    }

    return isRequired;
  };

  const isVerifyEmailRequired = (account): boolean => {
    if (typeof account.email_mandatory_completed === 'undefined') {
      return false;
    }

    return !account.email_mandatory_completed;
  };

  return {
    showMandatoryWall,
  };
};

export default useMandatoryWall;
